import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Pin, Plus, Megaphone, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Announcements() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [pinned, setPinned] = useState(false);

  const load = async () => {
    const { data } = await api.get("/announcements");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/announcements", { title, body, pinned });
      setTitle(""); setBody(""); setPinned(false); setOpen(false);
      toast.success("Announcement posted");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const remove = async (id) => {
    await api.delete(`/announcements/${id}`);
    load();
  };

  const sorted = [...items].sort((a, b) => (b.pinned - a.pinned));

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-purple-700 font-bold">Group</p>
          <h1 className="text-4xl sm:text-5xl font-black font-display tracking-tight">Announcements</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-announcement-btn" className="rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold btn-lift h-11 px-5">
              <Plus className="w-4 h-4 mr-1" /> New
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Post announcement</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="font-semibold">Title</Label>
                <Input data-testid="announcement-title-input" required value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 rounded-xl h-11" />
              </div>
              <div>
                <Label className="font-semibold">Message</Label>
                <Textarea data-testid="announcement-body-input" required value={body} onChange={(e) => setBody(e.target.value)} rows={5} className="mt-1 rounded-xl" />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={pinned} onCheckedChange={setPinned} data-testid="announcement-pin-toggle" disabled={!(user?.role === "admin" || user?.role === "owner")} />
                <Label className={`font-semibold ${!(user?.role === "admin" || user?.role === "owner") ? "text-slate-400" : ""}`}>
                  Pin to top {!(user?.role === "admin" || user?.role === "owner") && <span className="text-xs">(admin only)</span>}
                </Label>
              </div>
              <Button type="submit" data-testid="announcement-submit-btn" className="w-full rounded-2xl bg-purple-700 hover:bg-purple-800 h-11 font-bold">Post</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {sorted.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid gap-4">
          {sorted.map((a) => (
            <article key={a.id} data-testid={`announcement-card-${a.id}`}
              className={`relative rounded-3xl p-6 border ${a.pinned ? "bg-amber-50 border-amber-300" : "bg-white border-slate-100"} shadow-sm hover:shadow-md transition-shadow`}>
              {a.pinned && (
                <div className="absolute -top-2 left-6 bg-amber-500 text-slate-900 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full flex items-center gap-1">
                  <Pin className="w-3 h-3" /> Pinned
                </div>
              )}
              <div className="flex items-start gap-4">
                <Avatar className="w-11 h-11">
                  <AvatarImage src={a.author_picture} />
                  <AvatarFallback className="bg-purple-700 text-white font-bold">{a.author_name?.[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-black font-display text-slate-900">{a.title}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{a.author_name} · {new Date(a.created_at).toLocaleString()}</p>
                  <p className="mt-3 text-slate-700 whitespace-pre-wrap leading-relaxed">{a.body}</p>
                </div>
                {(a.author_id === user?.user_id || user?.role === "owner" || user?.role === "admin") && (
                  <button onClick={() => remove(a.id)} data-testid={`delete-announcement-${a.id}`} className="text-slate-400 hover:text-red-500 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-slate-200">
      <Megaphone className="w-12 h-12 text-purple-300 mx-auto" />
      <p className="mt-4 text-lg font-bold text-slate-700">No announcements yet</p>
      <p className="text-slate-500 text-sm">Be the first to share something with the group.</p>
    </div>
  );
}
